import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Icon, Field, Button, Badge } from "@/components/ui";
import Seo from "@/components/Seo";
import { ImmersiveShell } from "@/components/immersive/ImmersiveShell";
import { api } from "@/lib/api";

export default function VerifyEmail() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // verifying | pending | success | error
  const [name, setName] = useState("");
  const [err, setErr] = useState(null);
  const [email, setEmail] = useState(params.get("email") || "");
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const ran = useRef(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (ran.current) return; // guard against React.StrictMode double-invoke (token is single-use)
    ran.current = true;
    const token = params.get("token");
    if (!token) { setStatus("pending"); return; } // arrived from a signup form → show verify-by-code/link
    (async () => {
      try {
        const r = await api.post("/auth/verify-email", { token }, false);
        setName((r.full_name || "").split(" ")[0] || "");
        setStatus("success");
      } catch (e) { setErr(e?.message || "We couldn't verify this link."); setStatus("error"); }
    })();
  }, [params]);

  const verifyByCode = async () => {
    setMsg(null);
    try {
      const r = await api.post("/auth/verify-email-code", { email: email.trim(), code: code.trim() }, false);
      setName((r.full_name || "").split(" ")[0] || "");
      setStatus("success");
    } catch (e) { setMsg(e?.message || "That code didn't work. Check it and try again."); }
  };

  const resend = async () => {
    setMsg(null);
    setCooldown(30);
    try {
      await api.post("/auth/resend-verification", { email: email.trim(), origin_url: window.location.origin }, false);
      setMsg("If that email needs verifying, we've sent a new link and code. Check spam if it's not in your inbox.");
    } catch { setMsg("Couldn't resend right now — please try again shortly."); }
  };

  return (
    <ImmersiveShell>
      <Seo title="Verify email" path="/verify-email" description="Verify your HomecareMatches email address." />
      <section className="imm-wrap" data-testid="verify-email-screen">
        <div className="imm-legal">
          <div className="imm-contact-done" style={{ paddingTop: 12 }}>
            {status === "verifying" && (<>
              <Icon name="loader" size={40} color="var(--imm-sky)" />
              <h3>Verifying your email…</h3>
              <p>One moment while we confirm your address.</p>
            </>)}

            {(status === "pending" || status === "error") && (<>
              <Icon name="mail" size={44} color="var(--imm-sky)" />
              <h3 data-testid={status === "error" ? "verify-error" : "verify-pending"}>Account created — verify your email</h3>
              <p>{status === "error" ? `${err} ` : ""}Open the <b>verification link</b> we emailed you, or enter the <b>6-digit code</b> from that email below. Once verified, sign in from the menu in the top-left with your email and password.</p>
              <div style={{ maxWidth: 380, margin: "0 auto", width: "100%" }}>
                <Field testID="verify-email-input" label="Your email" value={email} onChange={setEmail} placeholder="you@example.com" inputMode="email" autoCapitalize="none" autoFocus={!email.trim()} />
                <Field testID="verify-code-input" label="6-digit code" value={code} onChange={setCode} placeholder="123456" inputMode="numeric" maxLength={6} autoFocus={!!email.trim()} onEnter={() => { if (email.trim() && code.trim().length >= 6) verifyByCode(); }} />
                {msg ? <div style={{ margin: "6px 0" }}><Badge testID="verify-msg" label={msg} tone={/sent/.test(msg) ? "success" : "warning"} /></div> : null}
                <Button testID="verify-code-submit" icon="check" label="Verify with code" onClick={verifyByCode} disabled={!email.trim() || code.trim().length < 6} />
                <div style={{ height: 8 }} />
                <Button testID="verify-resend" variant="secondary" icon="mail" label={cooldown > 0 ? `Didn't get it? Resend in ${cooldown}s` : "Didn't get it? Resend link & code"} onClick={resend} disabled={!email.trim() || cooldown > 0} />
              </div>
            </>)}

            {status === "success" && (<>
              <Icon name="check-circle" size={44} color="var(--success)" />
              <h3 data-testid="verify-success">Your account is created{name ? `, ${name}` : ""}!</h3>
              <p>Your email is verified. Go to the home page and use the menu in the top-left to sign in with your email and password.</p>
              <Button testID="verify-go-login" label="Sign in now" onClick={() => nav("/login")} />
              <div style={{ height: 8 }} />
              <Button testID="verify-go-home" variant="secondary" label="Back to home" onClick={() => nav("/")} />
            </>)}
          </div>
        </div>
      </section>
    </ImmersiveShell>
  );
}
